import { useEffect, useRef } from 'react';
import { isBrowser } from '../utils/dom.js';
import { useCallbackRef } from './use-callback-ref.js';

export type ScrollPosition = { scrollX: number; scrollY: number };

const getScrollPosition = (): ScrollPosition => ({
  scrollX: isBrowser ? window.pageXOffset : 0,
  scrollY: isBrowser ? window.pageYOffset : 0,
});

/**
 * This hook fires an effect when the scroll position changes. The effect will
 * be provided with the before/after scroll positions. Note that the effect may
 * not be always run: if scrolling is disabled through `useScrollController`, it
 * will be a no-op.
 */
export const useScrollPosition = (
  effect?: (
    position: ScrollPosition,
    lastPosition: ScrollPosition | null
  ) => void,
  deps: unknown[] = []
): void => {
  const dynamicEffect = useCallbackRef(effect);
  const lastPositionRef = useRef<ScrollPosition | null>(getScrollPosition());

  useEffect(() => {
    const handleScroll = () => {
      const currentPosition = getScrollPosition();
      dynamicEffect(currentPosition, lastPositionRef.current);
      lastPositionRef.current = currentPosition;
    };
    const opts: AddEventListenerOptions & EventListenerOptions = {
      passive: true,
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, opts);

    return () => window.removeEventListener('scroll', handleScroll, opts);
  }, [dynamicEffect, ...deps]);
};
